"use client";

/**
 * useCurrency - client-side currency state backed by the `ev_currency` cookie.
 *
 * The cookie is the single source of truth for the visitor's currency choice.
 * src/middleware.ts seeds it once from Vercel IP geo (US -> USD, everyone else,
 * incl. Canada -> CAD) when it is absent; the toggle in the pricing section
 * overwrites it and always wins. This hook lets independent client components
 * (Pricing, SmbPlansDropdown, FAQ) read and update that one value and stay in
 * sync without a shared React provider: writes broadcast a window event so
 * every mounted consumer re-renders together.
 *
 * SSR/hydration note: the server renders with DEFAULT_CURRENCY (CAD), then this
 * hook reads the real cookie on mount and re-renders. Prices are not
 * security-sensitive, so a one-frame default-then-correct is acceptable and
 * avoids a hydration mismatch.
 */

import { useCallback, useEffect, useState } from "react";
import { DEFAULT_CURRENCY, type Currency } from "@/lib/constants/pricing-display";

export const CURRENCY_COOKIE = "ev_currency";
// Written only on an EXPLICIT choice (modal button or header switch), never by
// the middleware geo-seed. The first-visit modal gates on this, so seeding the
// currency default does not suppress the prompt.
export const CURRENCY_CHOICE_COOKIE = "ev_currency_choice";
const CURRENCY_EVENT = "ev-currency-change";
/** One year, in seconds. */
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

function isCurrency(value: string | undefined): value is Currency {
  return value === "USD" || value === "CAD";
}

function readCookie(): Currency {
  if (typeof document === "undefined") return DEFAULT_CURRENCY;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${CURRENCY_COOKIE}=`));
  const value = match?.split("=")[1];
  return isCurrency(value) ? value : DEFAULT_CURRENCY;
}

function writeCookie(currency: Currency): void {
  if (typeof document === "undefined") return;
  document.cookie = `${CURRENCY_COOKIE}=${currency}; path=/; max-age=${COOKIE_MAX_AGE}; samesite=lax`;
  document.cookie = `${CURRENCY_CHOICE_COOKIE}=1; path=/; max-age=${COOKIE_MAX_AGE}; samesite=lax`;
}

export function useCurrency(): {
  currency: Currency;
  setCurrency: (next: Currency) => void;
} {
  // Start from the SSR default to keep the server and first client render in
  // agreement; sync to the cookie right after mount.
  const [currency, setCurrencyState] = useState<Currency>(DEFAULT_CURRENCY);

  useEffect(() => {
    setCurrencyState(readCookie());
    const onChange = (event: Event) => {
      const next = (event as CustomEvent<Currency>).detail;
      if (isCurrency(next)) setCurrencyState(next);
    };
    window.addEventListener(CURRENCY_EVENT, onChange);
    return () => window.removeEventListener(CURRENCY_EVENT, onChange);
  }, []);

  const setCurrency = useCallback((next: Currency) => {
    writeCookie(next);
    setCurrencyState(next);
    window.dispatchEvent(new CustomEvent<Currency>(CURRENCY_EVENT, { detail: next }));
  }, []);

  return { currency, setCurrency };
}
